import cookie from 'js-cookie'
import { STV_COOKIES } from '../../../../constants'
import getCurrentEpisodeFromStore from './get-video-data'
import prepareVideoWithAds from './prepare-video'
import getVideoFromBrightcove from './get-video-from-brightcove'
import brightcoveIds from '../config/brightcove-ids'
import { trackEvent, initConvivaTracking } from '../../../../utils/dtmHelpers'
import { getPlayerInfo, getProgrammeMetadata } from '../analytics/convivaAnalyitcs'

function setupEndboard(player, initialVideoData) {
  // Get Brightcove configuration
  const isProduction = window.parent.STV.environment === 'production'
  initConvivaTracking(cookie.get(STV_COOKIES.STV_USERID), isProduction)
  if (!player || !player.mediainfo) {
    throw new Error('Player instance or mediainfo not provided')
  }

  let endTime = null
  let countdownInterval = null
  let countdown = 10
  let isLoadingNextEpisode = false
  let endboardDisplayed = false
  let isAutoRestartCountdown = false
  let userInteracted = false

  const STATE = {
    IDLE: 'IDLE',
    PLAYER_NEXT_BUTTON: 'PLAYER_NEXT_BUTTON',
    FULL_END_BOARD: 'FULL_END_BOARD',
    LOADING: 'LOADING',
  }

  /**
   * Handles state transitions with validation
   * @param {string} newState - Target state from STATE enum
   */
  function transitionState(newState) {
    // Valid transitions matrix
    const validTransitions = {
      [STATE.IDLE]: [STATE.PLAYER_NEXT_BUTTON, STATE.FULL_END_BOARD],
      [STATE.PLAYER_NEXT_BUTTON]: [STATE.FULL_END_BOARD, STATE.LOADING, STATE.IDLE],
      [STATE.FULL_END_BOARD]: [STATE.LOADING, STATE.IDLE],
      [STATE.LOADING]: [STATE.IDLE],
    }

    if (validTransitions[currentState]?.includes(newState)) {
      currentState = newState
    }
  }

  let currentState = STATE.IDLE

  /**
   * Sets endTime from video metadata
   * @throws {Error} If missing end cue point
   */
  function initializeEndTime() {
    const endCuePoint = player.mediainfo.cuePoints.find(
      cuePoint => cuePoint.name === 'end'
    )

    if (!endCuePoint) {
      throw new Error('End cue point not found')
    }

    endTime = endCuePoint.time - countdown
    transitionState(STATE.IDLE)
    isLoadingNextEpisode = false

    if (countdownInterval) {
      clearInterval(countdownInterval)
      countdownInterval = null
    }
  }

  /**
   * Gets current episode data from store
   * @param {Object} params
   * @param {string} params.groupToken - Viewer's group token
   * @param {boolean} params.includeNext - Whether to fetch next episode
   * @returns {Promise<Object>} Video metadata
   */
  async function retrieveVideoData({ groupToken, includeNext }) {
    return getCurrentEpisodeFromStore({ groupToken, includeNext })
  }

  /**
   * Handles player time updates to trigger endboard display
   */
  function handleTimeUpdate() {
    if (endTime === null) return // End time not set

    const currentTime = player.currentTime()

    // Check if currentTime has reached or surpassed endTime
    if (
      currentTime >= endTime
      && currentState === STATE.IDLE
      && !isLoadingNextEpisode
    ) {
      transitionState(STATE.FULL_END_BOARD)
      displayEndboard()
    } else if (currentTime < endTime && currentState !== STATE.IDLE) {
      // User rewound before endTime, hide the endboard
      hideEndboard()
    }
  }

  async function displayEndboard() {
    const groupToken = cookie.get(STV_COOKIES.STV_GROUPTOKEN)
    const videoData = await retrieveVideoData({ groupToken, includeNext: true })
    const { recommendedProgramme } = videoData
    const isEndOfShow = !!recommendedProgramme
    transitionState(isEndOfShow ? STATE.SHOWING_END_OF_SHOW : STATE.SHOWING_END_OF_PLAY)

    // Create endboard overlay
    const endboardOverlay = document.createElement('div')
    endboardOverlay.className = 'vjs-endboard-overlay'

    // Add class to #stv-vod-player
    const vodPlayerContainer = document.querySelector('#stv-vod-player')
    if (vodPlayerContainer) {
      vodPlayerContainer.classList.remove('vjs-eos-mode', 'vjs-eop-mode')
      vodPlayerContainer.classList.add(isEndOfShow ? 'vjs-eos-mode' : 'vjs-eop-mode')
    }

    if (isEndOfShow) {
      trackEvent('recomPromotedProg', {
        eop_current_programme: videoData.programmeName,
        recom_shown: videoData?.nextEpisode?.programme?.name,
      })
      showRecommendedContent({
        overlay: endboardOverlay,
        recommendedProgramme, // This now contains the full object including accessibility
        currentProgrammeName: videoData.programmeName,
        nextEpisode: videoData.nextEpisode.guid,
      })
    } else if (videoData.nextEpisode) {
      trackEvent('recomPromotedProg',
        {
          eop_current_programme: videoData.programmeName,
          recom_shown: videoData.nextEpisode.guid,
        })
      showPlayerNextButton(endboardOverlay, videoData.nextEpisode)
    } else {
      // Handle case where there's no next episode or recommended programme
      hideEndboard()
      return
    }

    player.el().appendChild(endboardOverlay)

    endboardDisplayed = true
    // Start the countdown for both EoS and EoP states
    startCountdown(endboardOverlay, videoData.nextEpisode, { stopOnInteraction: true }, isEndOfShow)
  }

  function showRecommendedContent(props) {
    const {
      overlay,
      recommendedProgramme,
      currentProgrammeName,
      nextEpisode,
    } = props
    // Extract accessibility from the featured episode within the recommended programme
    const accessibility = recommendedProgramme?.featuredEpisode?.accessibility
    // Find the main image for the poster background
    const mainImage = recommendedProgramme?.images?.find(img => img.imageType === 'mainImage')
    if (mainImage?._filepath) {
      const posterImage = document.createElement('img')
      // Construct URL - assuming rendition logic is handled elsewhere or use a default size
      posterImage.src = mainImage._filepath.replace('[width]', '1920').replace('[height]', '1080')
      posterImage.className = 'vjs-recommended-poster'
      overlay.appendChild(posterImage)
    }

    // Create a container for programme info and buttons
    const infoContainer = document.createElement('div')
    infoContainer.className = 'vjs-recommended-info-container'

    // Add 'Because you watched: ' text
    const watchedText = document.createElement('p')
    watchedText.className = 'vjs-watched-text'
    watchedText.innerHTML = `Because you watched <strong>${currentProgrammeName}</strong>`
    infoContainer.appendChild(watchedText)

    // Add programme title
    const titleElement = document.createElement('h2')
    titleElement.className = 'vjs-recommended-title'
    titleElement.textContent = recommendedProgramme.name // Use 'name' instead of 'title'
    infoContainer.appendChild(titleElement)

    // Add meta icons if we have accessibility data or genre or forced ads
    const hasMetaData = accessibility
      || recommendedProgramme.genre?.name
      || recommendedProgramme.forceAds
    if (hasMetaData) {
      const metaIconsContainer = document.createElement('div')
      metaIconsContainer.className = 'vjs-meta-icons'

      // Genre
      if (recommendedProgramme.genre?.name) {
        const genreElement = document.createElement('span')
        genreElement.className = 'vjs-meta-genre'
        genreElement.textContent = recommendedProgramme.genre.name
        metaIconsContainer.appendChild(genreElement)
      }

      const separator = document.createElement('span')
      separator.innerHTML = '&bull;'
      separator.className = 'vjs-meta-separator'
      metaIconsContainer.appendChild(separator)

      // Accessibility Icons
      if (accessibility) {
        if (accessibility.subtitles) {
          const subtitleIcon = document.createElement('div')
          subtitleIcon.className = 'vjs-meta-icon'
          const subtitleImg = document.createElement('img')
          subtitleImg.src = 'icons/subtitle-icon.svg'
          subtitleImg.alt = 'Subtitles'
          subtitleIcon.appendChild(subtitleImg)
          subtitleIcon.setAttribute('title', 'Subtitles')
          metaIconsContainer.appendChild(subtitleIcon)
        }

        if (accessibility.guidance) {
          const guidanceIcon = document.createElement('div')
          guidanceIcon.className = 'vjs-meta-icon'
          const guidanceImg = document.createElement('img')
          guidanceImg.src = 'icons/guidance-icon.svg'
          guidanceImg.alt = 'Guidance'
          guidanceIcon.appendChild(guidanceImg)
          guidanceIcon.setAttribute('title', 'Guidance')
          metaIconsContainer.appendChild(guidanceIcon)
        }

        if (accessibility.audioDescription) {
          const audioIcon = document.createElement('div')
          audioIcon.className = 'vjs-meta-icon'
          const audioImg = document.createElement('img')
          audioImg.src = 'icons/audio-described-icon.svg'
          audioImg.alt = 'Audio Described'
          audioIcon.appendChild(audioImg)
          audioIcon.setAttribute('title', 'Audio Described')
          metaIconsContainer.appendChild(audioIcon)
        }

        if (accessibility.signLanguage) {
          const signedIcon = document.createElement('div')
          signedIcon.className = 'vjs-meta-icon'
          const signedImg = document.createElement('img')
          signedImg.src = 'icons/signed-icon.svg'
          signedImg.alt = 'Visually Signed'
          signedIcon.appendChild(signedImg)
          signedIcon.setAttribute('title', 'Visually Signed')
          metaIconsContainer.appendChild(signedIcon)
        }
      }

      // Forced Ads icon
      if (recommendedProgramme.forceAds) {
        const adIcon = document.createElement('div')
        adIcon.className = 'vjs-meta-icon vjs-ad-icon'
        const adImg = document.createElement('img')
        adImg.src = 'icons/forced-ads-icon.svg'
        adImg.alt = 'Contains ads'
        adIcon.appendChild(adImg)
        adIcon.setAttribute('title', 'Contains ads')
        metaIconsContainer.appendChild(adIcon)
      }

      infoContainer.appendChild(metaIconsContainer)
    }

    // Add programme summary
    const summaryElement = document.createElement('p')
    summaryElement.className = 'vjs-recommended-summary'
    summaryElement.textContent = recommendedProgramme.longDescription // This is correct
    infoContainer.appendChild(summaryElement)

    // Create button with countdown
    const recommendedButton = createCountdownButton(
      'Watch Recommended Programme',
      'Recommended programme starts in %s',
      'vjs-recommended-programme-button',
      nextEpisode
    )

    // const backToPreviousButton = document.createElement('button')
    // backToPreviousButton.textContent = 'Back to previous'
    // backToPreviousButton.className = 'vjs-back-to-previous-button'

    const buttonWrapper = document.createElement('div')
    buttonWrapper.className = 'vjs-eos-button-wrapper'

    buttonWrapper.appendChild(recommendedButton)
    // buttonWrapper.appendChild(backToPreviousButton)

    overlay.appendChild(infoContainer)
    overlay.appendChild(buttonWrapper)

    // Add CSS class to minimize player
    player.el().classList.add('miniPlayer')

    // Add mini player controls
    const miniControls = document.createElement('div')
    miniControls.className = 'vjs-mini-player-controls'

    const playPauseButton = document.createElement('button')
    playPauseButton.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
    </svg>`
    playPauseButton.title = 'Play/Pause'
    playPauseButton.onclick = (e) => {
      e.stopPropagation()
      if (player.paused()) {
        player.play()
        playPauseButton.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
        </svg>`
      } else {
        player.pause()
        playPauseButton.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M8 5v14l11-7z"/>
        </svg>`
      }
    }

    const fullscreenButton = document.createElement('button')
    fullscreenButton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor" transform="scale(-1, 1)">
      <path d="M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h280v80H200v560h560v-280h80v280q0 33-23.5 56.5T760-120H200Zm188-212-56-56 372-372H560v-80h280v280h-80v-144L388-332Z"/>
    </svg><span class="vjs-mini-player-watch-credits">Watch Credits</span>`
    fullscreenButton.title = 'Open in full size'
    fullscreenButton.className = 'vjs-mini-player-watch-credits-button'
    // Capture nextEpisode in closure for the fullscreen button
    const nextEpisodeData = nextEpisode
    fullscreenButton.onclick = (e) => {
      e.stopPropagation()
      const vodPlayerContainer = document.querySelector('#stv-vod-player')
      const miniPlayerControls = player.el().querySelector('.vjs-mini-player-controls')
      if (miniPlayerControls) {
        miniPlayerControls.remove()
      }
      // Remove EOS mode and switch to EOP mode
      if (vodPlayerContainer) {
        vodPlayerContainer.classList.remove('vjs-eos-mode')
        vodPlayerContainer.classList.add('vjs-eop-mode')
      }
      player.el().classList.remove('miniPlayer')
      // Show control bar when exiting mini player mode
      player.el().classList.remove('vjs-hide-controls')
      // Keep the existing countdown running, just update the display
      const endboardOverlay = document.createElement('div')
      transitionState(STATE.FULL_END_BOARD)
      endboardOverlay.className = 'vjs-endboard-overlay'

      // Create recommended programme button for control bar
      const fullviewRecommendedButton = createCountdownButton(
        'Watch Recommended Programme',
        'Recommended programme starts in %s',
        'vjs-next-episode-button',
        nextEpisodeData
      )

      // Add button to control bar
      const controlBar = player.el().querySelector('.vjs-control-bar')
      if (controlBar) {
        controlBar.appendChild(fullviewRecommendedButton)
      }
      // Replace old overlay with new one
      const oldEndboard = player.el().querySelector('.vjs-endboard-overlay')
      if (oldEndboard) {
        player.el().replaceChild(endboardOverlay, oldEndboard)
      } else {
        player.el().appendChild(endboardOverlay)
      }
      // Update button text based on interaction state
      if (userInteracted) {
        updateButtonsOnCountdownStop()
      }
      handleUserInteraction()
    }
    miniControls.appendChild(fullscreenButton)
    miniControls.appendChild(playPauseButton)
    player.el().appendChild(miniControls)
  }

  function showPlayerNextButton(overlay, nextEpisode) {
    transitionState(STATE.PLAYER_NEXT_BUTTON)
    // Create button with countdown
    const nextEpisodeButton = createCountdownButton(
      'Next episode',
      'Next episode starts in %s',
      'vjs-next-episode-button',
      nextEpisode
    )

    const controlBar = player.el().querySelector('.vjs-control-bar')
    controlBar.appendChild(nextEpisodeButton)
  }

  function createPlayIcon() {
    const playIconSvg = `
    <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2.93018 1.00049L13.8203 8.00131L2.93018 15.0021V1.00049Z" fill="#151515"/>
    </svg>`
    const playIcon = document.createElement('span')
    playIcon.className = 'vjs-play-icon'
    playIcon.innerHTML = playIconSvg
    return playIcon
  }

  /**
   * Creates a button with countdown functionality
   * @param {string} text - Button text
   * @param {string} countdownTemplate - Template for countdown text using %s for seconds
   * @param {string} className - CSS class for the button
   * @param {Object} nextContent - Next content to play when clicked
   * @returns {HTMLButtonElement} Button element with countdown
   */
  function createCountdownButton(text, countdownTemplate, className, nextContent) {
    const button = document.createElement('button')
    button.dataset.staticText = text
    button.dataset.countdownTemplate = countdownTemplate
    button.className = className
    button.onclick = () => {
      if (text.includes('Recommended')) trackEvent('recomNextProg', { promoted_next_clicked: 1, promoted_programme: nextContent?.programme?.name })
      else trackEvent('recomNextEp', { recom_next_clicked: 1, next_episode: nextContent.guid })

      if (!isLoadingNextEpisode) {
        loadNextEpisode(nextContent, false, false)
      }
    }

    // Add play icon
    const playIconElEl = createPlayIcon()
    button.prepend(playIconElEl)

    // Add countdown display
    const countdownDisplay = document.createElement('span')
    countdownDisplay.className = 'vjs-endboard-countdown'
    countdownDisplay.textContent = countdownTemplate.replace('%s', countdown)
    button.appendChild(countdownDisplay)

    return button
  }

  /**
   * Updates button text when countdown is stopped
   * Removes countdown display and shows static text
   */
  function updateButtonsOnCountdownStop() {
    // Update all countdown buttons (both next episode and recommended)
    const targetButtons = Array.from(
      player.el().querySelectorAll('.vjs-next-episode-button, .vjs-recommended-programme-button')
    )
    targetButtons.forEach((oldButton) => {
      const countdownEl = oldButton.querySelector('.vjs-endboard-countdown')
      if (countdownEl) {
        const { staticText } = oldButton.dataset
        // Create new button with same properties but without countdown
        const newButton = document.createElement('button')
        newButton.className = oldButton.className
        newButton.onclick = oldButton.onclick
        newButton.append(createPlayIcon(), document.createTextNode(staticText))
        // Replace old button with new one
        oldButton.replaceWith(newButton)
      }
    })
  }
  /**
   * Starts countdown timer for automatic content transition
   * @param {HTMLElement} overlay - The endboard overlay element
   * @param {Object} nextContent - Data for the next content to play
   * @note Tracks:
   * @param {Object} options - Optional configuration
   * @param {boolean} options.stopOnInteraction - Whether to stop countdown on user interaction
   * - Total transition time
   * - Content type (EoP/EoS)
   * - Transition success status
   */
  // eslint-disable-next-line max-len
  function startCountdown(overlay, nextContent, options = { stopOnInteraction: true }, isEndOfShow) {
    // Reset countdown
    countdown = 10

    // Update buttons immediately
    updateButtonsToShowCountdown()

    // Start the countdown
    if (countdownInterval) {
      clearInterval(countdownInterval)
      countdownInterval = null
    }

    countdownInterval = setInterval(() => {
      // If user has interacted, stop countdown
      if (options.stopOnInteraction && userInteracted && !isAutoRestartCountdown) {
        clearInterval(countdownInterval)
        countdownInterval = null
      }
      countdown -= 1
      if (countdown <= 0) {
        loadNextEpisode(nextContent, true, isEndOfShow)
        clearInterval(countdownInterval)
        countdownInterval = null
      } else {
        // Update button countdown displays
        updateButtonsToShowCountdown()
      }
    }, 1000)
  }

  /**
   * Performs thorough cleanup of all endboard-related elements
   */
  function cleanupEndboardElements() {
    // List of all selectors for elements that need cleanup
    const elementsToRemove = [
      '.vjs-endboard-overlay',
      '.vjs-mini-player-controls',
      '.vjs-next-episode-button',
      '.vjs-recommended-programme-button',
      '.vjs-recommended-poster',
      '.vjs-recommended-info-container',
      '.vjs-eos-button-wrapper',
      '.vjs-endboard-countdown',
    ]

    // Remove all matching elements
    elementsToRemove.forEach((selector) => {
      const elements = player.el().querySelectorAll(selector)
      elements.forEach(element => element.parentNode?.removeChild(element))
    })
  }

  /**
   * Hides the endboard overlay and resets relevant flags and timers.
   */
  function hideEndboard() {
    // Reset state
    transitionState(STATE.IDLE)

    // Remove the endboard overlay
    const endboardOverlay = player.el().querySelector('.vjs-endboard-overlay')
    if (endboardOverlay) {
      endboardOverlay.remove()
    }

    // Remove the next button if it exists in control bar
    const nextButton = player.el().querySelector('.vjs-control-bar .vjs-next-episode-button')
    if (nextButton) {
      nextButton.remove()
    }

    // Remove miniPlayer class if it exists
    player.el().classList.remove('miniPlayer')

    // Remove EoS/EoP classes from #stv-vod-player
    const vodPlayerContainer = document.querySelector('#stv-vod-player')
    if (vodPlayerContainer) {
      vodPlayerContainer.classList.remove('vjs-eos-mode', 'vjs-eop-mode')
    }

    // Reset variables
    endboardDisplayed = false
    userInteracted = false
    isAutoRestartCountdown = false
    countdown = 10
    if (countdownInterval) {
      clearInterval(countdownInterval)
      countdownInterval = null
    }
  }

  /**
   * Updates button text to show countdown
   * Restores countdown display on buttons
   */
  function updateButtonsToShowCountdown() {
    const targetButtons = Array.from(
      player.el().querySelectorAll('.vjs-next-episode-button, .vjs-recommended-programme-button')
    ).concat(Array.from(player.el().querySelectorAll('.vjs-recommended-programme-button, .vjs-eos-button-wrapper button')))
    targetButtons.forEach((button) => {
      const { countdownTemplate } = button.dataset
      if (countdownTemplate) {
        // Create new button with same properties
        const newButton = document.createElement('button')
        newButton.className = button.className
        // Copy data attributes
        newButton.dataset.staticText = button.dataset.staticText
        newButton.dataset.countdownTemplate = button.dataset.countdownTemplate
        // Copy click handler
        newButton.onclick = button.onclick
        // Create countdown display
        const countdownDisplay = document.createElement('span')
        countdownDisplay.className = 'vjs-endboard-countdown'
        countdownDisplay.textContent = countdownTemplate.replace('%s', countdown)
        newButton.append(createPlayIcon(), countdownDisplay)
        button.replaceWith(newButton)
      }
    })
  }

  /**
   * Handles user interactions such as pausing, seeking, or any activity.
   * Cancels the countdown and displays a static "Next Episode" or "Recommended Programme" button.
   */
  function handleUserInteraction() {
    if (!userInteracted && endboardDisplayed && !isAutoRestartCountdown) {
      userInteracted = true
      if (countdownInterval) {
        clearInterval(countdownInterval)
        countdownInterval = null
      }
      // Reset countdown to prevent auto-play
      countdown = 0
      // Update buttons to show static text
      updateButtonsOnCountdownStop()
    }
  }

  /**
   * Initiates a non-stoppable countdown after video completion
   * @param {Object} nextEpisode - Next episode data
   */
  async function restartCountdownAfterCompletion(nextEpisode) {
    // Skip if no next episode data
    if (!nextEpisode) return

    // Clear any existing interval before restarting
    if (countdownInterval) {
      clearInterval(countdownInterval)
      countdownInterval = null
    }

    // Set auto-restart flag
    isAutoRestartCountdown = true

    // Reset user interaction flag
    userInteracted = false

    // Get video data to determine content type
    const groupToken = cookie.get(STV_COOKIES.STV_GROUPTOKEN)
    const videoData = await retrieveVideoData({ groupToken, includeNext: true })
    const { recommendedProgramme } = videoData
    const isEndOfShow = !!recommendedProgramme

    // Create endboard overlay
    const endboardOverlay = document.createElement('div')
    endboardOverlay.className = 'vjs-endboard-overlay'

    // Add appropriate class to player container
    const vodPlayerContainer = document.querySelector('#stv-vod-player')
    if (vodPlayerContainer) {
      vodPlayerContainer.classList.remove('vjs-eos-mode', 'vjs-eop-mode')
      vodPlayerContainer.classList.add(isEndOfShow ? 'vjs-eos-mode' : 'vjs-eop-mode')
    }

    // Show appropriate content
    if (isEndOfShow) {
      trackEvent('recomPromotedProg', {
        eop_current_programme: videoData.programmeName,
        recom_prog: videoData?.nextEpisode?.programme?.name,
      })
      showRecommendedContent({
        overlay: endboardOverlay,
        recommendedProgramme,
        currentProgrammeName: videoData.programmeName,
        nextEpisode: videoData.nextEpisode,
      })
    } else if (videoData.nextEpisode) {
      trackEvent('recomNextEp',
        {
          eos_current_programme: videoData.programmeName,
          next_ep: videoData.nextEpisode,
        })
      showPlayerNextButton(endboardOverlay, videoData.nextEpisode)
    }

    // Reset countdown
    countdown = 10

    player.el().appendChild(endboardOverlay)
    endboardDisplayed = true

    // Start countdown that ignores user interaction
    startCountdown(endboardOverlay, nextEpisode, { stopOnInteraction: false }, isEndOfShow)
  }

  /**
   * Loads next episode content
   * @param {Object} nextEpisode - The data for the next episode to load.
   * @throws {Error} On Brightcove load failure
   * @emits EPISODE_CHANGED - Notifies parent window of content change
   */
  async function loadNextEpisode(nextEpisode, isAutoplay, isEndOfShow) {
    if (isAutoplay) {
      if (isEndOfShow) trackEvent('recomNextProg', { promoted_next_auto: 1, promoted_programme: nextEpisode?.programme?.name })
      else trackEvent('recomNextEp', { recom_next_auto: 1, next_episode: nextEpisode.guid })
    }
    if (isLoadingNextEpisode) {
      return
    }

    isLoadingNextEpisode = true
    transitionState(STATE.LOADING)

    // Use the Brightcove ID from nextEpisodeData to load the video
    const brightcoveVideoId = nextEpisode.video.id

    try {
      // Determine DRM requirement for the next episode
      const drmRequired = !!nextEpisode?.programme?.drmEnabled
      // Fetch the correct Brightcove credentials based on DRM status
      const {
        ACCOUNT_ID,
        POLICY_KEY,
        AD_CONFIG_ID,
        CONFIG_ID,
      } = brightcoveIds(drmRequired, isProduction)

      // Fetch video data using the helper function and dynamically determined credentials
      const video = await getVideoFromBrightcove(
        brightcoveVideoId,
        ACCOUNT_ID,
        AD_CONFIG_ID,
        POLICY_KEY,
        CONFIG_ID
      )

      if (video instanceof Error) {
        isLoadingNextEpisode = false
        return
      }

      const videoObject = prepareVideoWithAds({
        player,
        video,
        content: nextEpisode,
        videoData: {
          ...nextEpisode,
          isPlayerPlus: initialVideoData?.isPlayerPlus,
          resumePosition: 0,
        },
        resumePosition: 0,
      })

      // Load and play the new video
      player.catalog.load(videoObject)
      const metaData = getProgrammeMetadata(nextEpisode)
      const playerInfo = getPlayerInfo()
      // add the metadata to setContentInfo when load
      player.on('loadedmetadata', () => {
        window?.ConvivaVideoAnalytics.setContentInfo(metaData)
        window?.ConvivaVideoAnalytics.setPlayerInfo(playerInfo)
      })

      player.play()
      // Ensure video element is visible again after being hidden on 'ended'
      const videoElement = player.el()?.querySelector('video')
      if (videoElement) {
        videoElement.style.display = ''
      }

      endboardDisplayed = false
      isLoadingNextEpisode = false
      countdown = 10

      // Clear the countdown interval
      if (countdownInterval) {
        clearInterval(countdownInterval)
        countdownInterval = null
      }
      // Clean up all endboard-related elements
      cleanupEndboardElements()
      const vodPlayerContainer = document.querySelector('#stv-vod-player')
      if (vodPlayerContainer) {
        vodPlayerContainer.classList.remove('vjs-eos-mode', 'vjs-eop-mode')
      }

      // Remove mini player controls if they exist
      const miniControls = player.el().querySelector('.vjs-mini-player-controls')
      if (miniControls) {
        miniControls.remove()
      }

      // Inform the parent window about the episode change
      window.parent.postMessage(
        {
          type: 'EPISODE_CHANGED',
          episodeData: {
            episodeId: nextEpisode.episodeId,
          },
        },
        window.location.origin
      )
    } catch (error) {
      isLoadingNextEpisode = false
      transitionState(STATE.IDLE)
    }
  }

  // Listen for video load events to ensure endTime is set correctly
  player.on('loadedmetadata', () => {
    initializeEndTime()
  })

  // Listen for time updates to trigger endboard
  player.on('timeupdate', handleTimeUpdate)

  // Event listeners for user interactions
  player.on('pause', handleUserInteraction)
  player.on('seeked', handleUserInteraction)
  player.on('useractive', handleUserInteraction)
  player.on('play', handleUserInteraction)
  player.on('volumechange', handleUserInteraction)
  player.on('fullscreenchange', handleUserInteraction)

  // Listen for video end
  player.on('ended', async () => { // Make the handler async
    // Fetch video data including next episode and recommended programme
    const groupToken = cookie.get(STV_COOKIES.STV_GROUPTOKEN)
    const videoData = await retrieveVideoData({
      groupToken,
      includeNext: true,
    })
    const { recommendedProgramme, nextEpisode } = videoData

    if (nextEpisode && !recommendedProgramme) {
      await loadNextEpisode(nextEpisode, true, false)
    } else {
      await restartCountdownAfterCompletion(nextEpisode) // This displays the overlay/buttons

      const videoElement = player.el()?.querySelector('video')
      if (videoElement) {
        videoElement.style.display = 'none'
      }
    }
  })

  // Ensure cleanup when the player is disposed
  player.on('dispose', () => {
    if (countdownInterval) {
      clearInterval(countdownInterval)
    }
    player.off('timeupdate', handleTimeUpdate)
    player.off('pause', handleUserInteraction)
    player.off('seeked', handleUserInteraction)
    player.off('useractive', handleUserInteraction)
    player.off('play', handleUserInteraction)
    player.off('volumechange', handleUserInteraction)
    player.off('ended', restartCountdownAfterCompletion)
    player.off('fullscreenchange', handleUserInteraction)
    player.off('loadedmetadata', initializeEndTime)
  })

  // Initialize endTime on setup
  initializeEndTime()

  return {
    loadNextEpisode,
    hideEndboard,
  }
}

export default setupEndboard
